import * as React from 'react';

export interface BottomTab {
  key: string;
  label: string;
  icon: React.ReactNode;
  onSelect: () => void;
  current?: boolean;
  /**
   * Count drawn on the icon — how many citations are still waiting to be sent, in the officer's
   * app. Omitted, and not zero, when there is nothing to report: a badge showing "0" is a mark on
   * the screen that means "nothing", which is worse than no mark at all.
   */
  badgeCount?: number;
}

export interface BottomTabBarProps {
  tabs: BottomTab[];
  className?: string;
}

/**
 * Fixed 5-destination bottom navigation (DESIGN_SYSTEM.md §3): icon + label
 * always visible, active tab in `--lx-primary`, >=44px touch target, and
 * `env(safe-area-inset-bottom)` padding baked into `.lx-bottom-tab-bar`.
 */
export function BottomTabBar({ tabs, className }: BottomTabBarProps): React.JSX.Element {
  return (
    // `data-lx-bottom-chrome` marks this as covering the bottom of the viewport, so a dropdown
    // opening near it stops above it instead of running underneath (see components/Select.tsx).
    <nav
      className={['lx-bottom-tab-bar', className].filter(Boolean).join(' ')}
      aria-label="primary"
      data-lx-bottom-chrome=""
    >
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className="lx-bottom-tab-bar__tab"
          aria-current={tab.current ? 'page' : undefined}
          onClick={tab.onSelect}
        >
          <span className="lx-bottom-tab-bar__icon" aria-hidden="true">
            {tab.icon}
            {tab.badgeCount && tab.badgeCount > 0 ? (
              <span className="lx-bottom-tab-bar__badge">{tab.badgeCount}</span>
            ) : null}
          </span>
          <span className="lx-bottom-tab-bar__label">{tab.label}</span>
        </button>
      ))}
    </nav>
  );
}
